// 任務元件
'use client';

import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { userState } from '@/slices/userSlice';
import { originURL } from '@/slices/originURLSlice';
import {
    setLoginModal,
    setCampaignHint,
    setCampaignHintModal,
    setCampaignUserInfo,
    popState,
    setCampaignBusyModal,
} from '@/slices/popSlice';
import _ from 'lodash';

import campaignClient from '@/common/apiClient/campaignClient';
import Image from 'next/image';

const CampaignTaskComponent = ({ task, order, spinSlug }) => {
    const { user } = useSelector(userState);
    const { apiUrl } = useSelector(originURL);

    const { campaignUserInfo } = useSelector(popState);
    const dispatch = useDispatch();
    const [isVerified, setIsVerified] = useState(false); // 是否驗證領取
    const [isLoading, setIsLoading] = useState(false); // 處理中

    // 是否完成任務
    const taskFinished = task?.isFinish || isVerified;

    // 檢查是否能執行動作
    const canTaskAction = useCallback(() => {
        if (!user?.uid) {
            dispatch(setLoginModal(true));
            return false;
        }
        if (isLoading) {
            handleBusy();
            return false;
        }
        return true;
    }, [user, isLoading]);

    // 前往任務 (暫時不用)
    const goToTask = async () => {
        // 先檢查
        if (!user?.uid) {
            dispatch(setLoginModal(true));
            return;
        }

        if (!taskFinished) {
            try {
                const result = await campaignClient.goTask(spinSlug, task?.id);
                // 如果成功的話跳轉連結
                if (result.status === 'success') window.open(result.link, '_blank');
            } catch (e) {
                console.error(e);
                if (e.errMsg.includes('same time')) handleBusy();
            }
        }
    };

    // 領取獎勵
    const receivePrize = async () => {
        // 先檢查
        if (!canTaskAction()) return;
        setIsLoading(true);

        try {
            // 驗證任務
            const result = await campaignClient.verifyTask(spinSlug, task?.id);

            // 如果尚未完成任務 跳提示
            if (result.message === 'fail') {
                dispatch(setCampaignHint(2));
                dispatch(setCampaignHintModal(true));
            } else {
                // 成功領取獎勵
                setIsVerified(true);
                dispatch(
                    setCampaignUserInfo({
                        ...campaignUserInfo,
                        leftSpinTimes: campaignUserInfo?.leftSpinTimes + 1,
                    })
                );
                // 跳獲得轉盤機會提示
                dispatch(setCampaignHint(1));
                dispatch(setCampaignHintModal(true));
            }
        } catch (e) {
            console.error(e);
            if (e.errMsg.includes('same time')) handleBusy();
        }
    };

    // 操作過於頻繁
    const handleBusy = () => {
        dispatch(setCampaignHintModal(false));
        dispatch(setCampaignBusyModal(true));
    };

    // 防止連點
    // const goToTaskThrottle = useCallback(_.throttle(goToTask, 1000, { trailing: false }));
    const receiveDebounce = useCallback(_.debounce(receivePrize, 700), [receivePrize]);

    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => setIsLoading(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    return (
        <div
            className={`between-center border-1 rounded-[6px] px-[25px] py-[8px] gap-x-[10px]
            ${
                taskFinished
                    ? 'bg-[#29AFCB] bg-opacity-[.52] border-transparent'
                    : 'bg-[#fff] bg-opacity-[.1] border-[#fff] border-opacity-[.3] umd:flex-col umd:items-start'
            } umd:px-[16px] umd:gap-y-[12px]`}
        >
            <div className="flex items-center gap-x-[18px] flex-grow umd:gap-x-[14px] uxs:!gap-x-[10px]">
                {/* 順序 */}
                <span className="tabular-nums text-[#fff] text-h3 uxs:hidden">
                    {order < 10 ? '0' + order : order}
                </span>

                {/* 分隔線 */}
                <span
                    className={`w-[1px] h-[21px] border-l-1 border-[${
                        taskFinished ? '#c7c7c7' : '#727272'
                    }] uxs:hidden`}
                />

                {/* 任務名稱 */}
                <span className="text-bd1m text-[#fff]">{task?.name}</span>
            </div>

            <div
                className={`flex items-center space-x-[10px] [&>button]:text-[#fff] [&>button]:rounded-[4px] [&>button]:px-[22px] [&>button]:py-[6px] [&>button]:text-btn1 [&>button]:trans
            [&>button]:backdrop-blur-[10px] [&>button]:inline-block [&>a]:text-[#fff] [&>a]:rounded-[4px] [&>a]:px-[22px] [&>a]:py-[6px] [&>a]:text-btn1 [&>a]:trans
            [&>a]:backdrop-blur-[10px] [&>a]:inline-block flex-shrink-0 ${
                taskFinished ? '' : 'umd:w-full [&>button]:umd:w-full [&>a]:umd:w-full'
            }`}
            >
                {taskFinished ? (
                    // 任務完成
                    <p className="flex items-center gap-x-[10px]">
                        <Image
                            width={28}
                            height={28}
                            src="/images/campaign/icon_finish.svg"
                            alt="finish"
                        />
                        <span className="text-btn1 text-[#fff] usm:hidden">已獲得輪盤次數！</span>
                    </p>
                ) : (
                    // 任務未完成
                    <>
                        {user?.uid ? (
                            <a
                                href={apiUrl + `spin/${spinSlug}/go-task/${task?.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="bg-[#fff] bg-opacity-[.12] hover:bg-opacity-[.35] text-center"
                            >
                                前往任務
                            </a>
                        ) : (
                            <button
                                onClick={() => dispatch(setLoginModal(true))}
                                className="bg-[#fff] bg-opacity-[.12] hover:bg-opacity-[.35]"
                            >
                                前往任務
                            </button>
                        )}

                        <button
                            onClick={receiveDebounce}
                            className="bg-[#29AFCb] bg-opacity-[.55]  hover:bg-opacity-[.85]"
                        >
                            驗證任務
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default CampaignTaskComponent;
